import React from 'react';
import {motion, MotionValue, useReducedMotion} from 'motion/react';

import styles from './chips-overlay.module.scss';
import {useChipsScroll} from '../../../../hooks/use-chips-scroll';
import {useScrollState} from '../../../../hooks/use-scroll-state';
import {Chip} from '../../chip/chip.react-component';
import {ChipType} from '../../types';

interface ChipsOverlayProps {
    scrollProgress?: MotionValue<number>;
}

const CHIP_POSITIONS = [
    {
        type: ChipType.PITCHING,
        position: {left: '1%', top: '23%'},
    },
    {
        type: ChipType.BRAND_EXPERIENCE,
        position: {left: '12%', bottom: '-3%'},
    },
    {
        type: ChipType.MARKETING,
        position: {left: '27%', top: '23%'},
    },
    {
        type: ChipType.SALES,
        position: {left: '90%', bottom: '-3%'},
    },
    {
        type: ChipType.HYPE_AND_PUBLICITY,
        position: {left: '130%', top: '23%'},
    },
    {
        type: ChipType.SCALABILITY,
        position: {left: '200%', top: '23%'},
    },
    {
        type: ChipType.SOFT_SKILLS,
        position: {left: '230%', bottom: '-3%'},
    },
] as const;

export const ChipsOverlay: React.FC<ChipsOverlayProps> = ({scrollProgress}) => {
    const {isStatic, effectiveScrollProgress} = useScrollState(scrollProgress);
    const shouldReduceMotion = useReducedMotion();
    const scrollAnimations = useChipsScroll(effectiveScrollProgress);

    const {chipsX: animatedChipsX, containerScale: animatedContainerScale} = scrollAnimations;

    if (isStatic) {
        return null;
    }

    return (
        <motion.div
            className={styles['chips-overlay']}
            style={{
                x: shouldReduceMotion ? 0 : animatedChipsX,
                scale: shouldReduceMotion ? 1 : animatedContainerScale,
            }}
            role="presentation"
            aria-hidden="true"
        >
            {CHIP_POSITIONS.map((chipConfig, index) => {
                const baseDuration = shouldReduceMotion ? 0.2 : 0.6;

                return (
                    <motion.div
                        key={`chip-${chipConfig.type}-${index}`}
                        className={styles['chip-position']}
                        style={chipConfig.position}
                        initial={shouldReduceMotion ? false : {opacity: 0, scale: 0.8, y: 20}}
                        animate={{opacity: 1, scale: 1, y: 0}}
                        transition={{
                            duration: baseDuration,
                            ease: 'easeOut',
                        }}
                    >
                        <Chip type={chipConfig.type} variant="inline" />
                    </motion.div>
                );
            })}
        </motion.div>
    );
};
